import { subject as caslSubject } from "@casl/ability";
import { buildAbility, type PermissionUser } from "./abilities";
import { type CitadelFilter, rulesToFilter } from "./filter";
import { ForbiddenError } from "./forbidden-error";
import type { ActionFor, AppAbility, AppSubjectName, CitadelOptions } from "./types";

// Raised when `assert` receives a row that lacks a column the pair's rules
// condition on. This is a programming error surfacing loudly (500), not a
// refusal (403): the query projected too few columns for the rules that govern
// it, and evaluating anyway would drop the very rule that should have decided
// the check. The message names rule internals — the error middleware logs it
// and never returns it verbatim.
export class IncompleteResourceError extends Error {
	constructor(action: string, subject: string, key: string) {
		super(
			`cannot assert ${action} ${subject}: the resource is missing "${key}", which a rule's conditions reference — the query projected too few columns`,
		);
		this.name = "IncompleteResourceError";
	}
}

export class Citadel {
	private ability: AppAbility;
	public readonly isOwner: boolean;
	public readonly userId: string;
	public readonly orgId: string;
	public readonly projectIds: string[];

	constructor(options: CitadelOptions) {
		this.userId = options.userId;
		this.orgId = options.orgId;
		this.isOwner = options.isOwner;
		this.projectIds = options.projectIds;

		const user: PermissionUser = {
			id: options.userId,
			orgId: options.orgId,
			isOwner: options.isOwner,
			projectIds: options.projectIds,
		};

		this.ability = buildAbility(user, options.permissions);
	}

	// Generic over the subject so `action` is checked against exactly the
	// actions that subject declares in the manifest.
	can<S extends AppSubjectName>(
		action: ActionFor<S>,
		subjectName: S,
		resource?: Record<string, unknown>,
	): boolean {
		const s = resource ? caslSubject(subjectName, resource) : subjectName;
		return this.ability.can(action, s);
	}

	cannot<S extends AppSubjectName>(
		action: ActionFor<S>,
		subjectName: S,
		resource?: Record<string, unknown>,
	): boolean {
		return !this.can(action, subjectName, resource);
	}

	// The row check: throws ForbiddenError unless the caller may perform the
	// action on this concrete resource. The resource is required — and guarded
	// at runtime, because an undefined resource (a bad cast, a missed await)
	// would degrade the assertion into a category check that any conditioned
	// rule passes. Category checks belong to can() and the route gate.
	assert<S extends AppSubjectName>(
		action: ActionFor<S>,
		subjectName: S,
		resource: Record<string, unknown>,
	): void {
		if (!resource) {
			throw new Error(
				`Citadel.assert(${String(action)}, ${subjectName}) was called without a resource — use can() for category checks`,
			);
		}
		this.assertResourceComplete(action, subjectName, resource);
		if (this.cannot(action, subjectName, resource)) {
			throw new ForbiddenError(String(action), subjectName);
		}
	}

	// The projection guard behind assert: every condition key of the rules
	// relevant to the pair must be present on the row. A missing key is not a
	// mismatch CASL can judge — a condition naming an absent field simply never
	// matches, so the rule drops out of the evaluation rather than applying to
	// it. In an additive model that always fails closed, which is why the guard
	// is not a security boundary; it is a debuggability one. The refusal would
	// be caused by the query's column list rather than by anything about the
	// caller, and a 403 with no explanation costs far more to chase down than a
	// 500 naming the missing column. `Object.hasOwn` keeps a legitimate null
	// column distinct from a column the query never selected.
	private assertResourceComplete<S extends AppSubjectName>(
		action: ActionFor<S>,
		subjectName: S,
		resource: Record<string, unknown>,
	): void {
		for (const rule of this.ability.rulesFor(String(action), subjectName)) {
			if (!rule.conditions) continue;
			for (const key of Object.keys(rule.conditions)) {
				if (!Object.hasOwn(resource, key)) {
					throw new IncompleteResourceError(String(action), subjectName, key);
				}
			}
		}
	}

	// The caller's merged, interpolated conditions for one action/subject pair,
	// ready for a database adapter to fold into a WHERE clause. `null` means
	// unrestricted; no surviving grant throws ForbiddenError. See filter.ts for
	// the merge and fail-closed semantics.
	filterFor<S extends AppSubjectName>(action: ActionFor<S>, subjectName: S): CitadelFilter | null {
		return rulesToFilter(this.ability, String(action), subjectName);
	}

	// Whether the caller holds the pair with no condition at all — `manage all`,
	// or a grant that names no scope. That is the owner and the admin role: an
	// organisation-wide grant from the editor still carries the caller's own
	// organisation as a condition and is not this. Business rules that treat an
	// administrator differently from everybody else — who may change a record
	// after its date has passed, for one — ask this question rather than
	// re-deriving it from filterFor's null, so the answer is the same on the
	// server and in the client.
	isUnrestrictedFor<S extends AppSubjectName>(action: ActionFor<S>, subjectName: S): boolean {
		if (this.cannot(action, subjectName)) return false;
		return this.filterFor(action, subjectName) === null;
	}

	/**
	 * The projects whose tickets the caller may create, change, delete and arrange on a board —
	 * `"all"` for owners and admins (a `manage all` wildcard), otherwise the caller's own
	 * projects. Ticket is deliberately not a manifest subject: this access is a relationship to
	 * the project, fixed in code, and `projectIds` is that relationship's claim — the projects
	 * the caller manages or deputises, plus the ones they are actively substituting in (the
	 * resolver folds the substitution windows into the claim).
	 *
	 * @returns `"all"`, or the project ids the caller's relationship covers.
	 */
	editableTicketProjects(): "all" | readonly string[] {
		return this.can("manage", "all") ? "all" : this.projectIds;
	}

	/**
	 * Whether the caller may create, change or delete the tickets of one project — the fixed
	 * rule behind every write on the ticket record itself: project members, their active
	 * substitutes, and admins. Reading is not asked here; every member of the organisation
	 * reads every ticket. Placing a ticket on a board is the wider `canArrangeTickets` below.
	 *
	 * `"all"` is a wildcard over the caller's organisation, not over every project the caller can
	 * see: a partner's admin reads the host's projects on a guest board, and holds no right over
	 * them. A surface that knows the project's organisation passes it, and a foreign one refuses
	 * whatever the reach says.
	 *
	 * @param projectId - The project the ticket belongs to.
	 * @param projectOrgId - The project's organisation, where the surface has it.
	 * @returns True when the caller's relationship to the project covers ticket work.
	 */
	canEditTickets(projectId: string, projectOrgId?: string | null): boolean {
		if (this.isForeignOrganization(projectOrgId)) return false;
		const reach = this.editableTicketProjects();
		return reach === "all" || reach.includes(projectId);
	}

	/**
	 * Whether a row's organisation, where a surface knows it, is another than the caller's. An
	 * unknown organisation is not foreign: the question is then answered by the reach alone, as it
	 * was before the organisation was asked.
	 */
	isForeignOrganization(orgId: string | null | undefined): boolean {
		return !!orgId && orgId !== this.orgId;
	}

	/**
	 * The throwing form of `canEditTickets`, for the routes and handlers that refuse with a 403.
	 *
	 * The refusal names the relationship rather than a manifest pair, because Ticket is not a
	 * subject: no `action:subject` spelling would be truthful. The message is developer-facing —
	 * the error middleware answers its fixed 403 shape and never forwards it.
	 *
	 * @param projectId - The project the ticket belongs to.
	 * @throws ForbiddenError when the caller's relationship does not cover the project.
	 */
	assertCanEditTickets(projectId: string): void {
		if (!this.canEditTickets(projectId)) {
			throw new ForbiddenError("edit", "the project's tickets");
		}
	}

	/**
	 * The projects whose work the caller may arrange on a board — put a ticket on a crew,
	 * reorder it, take it off. Wider than `editableTicketProjects` by exactly one arm: an
	 * assignment changes no ticket, it says when which crew does the work, so `update Plan` —
	 * the board-management right an organisation grants through the grid — opens arranging for
	 * every project, while the project relationship keeps opening it for one's own. The ticket
	 * record itself stays with `canEditTickets`, whatever this answers.
	 *
	 * @returns `"all"` under `update Plan` or the admin wildcard, otherwise the caller's own
	 *   projects (memberships plus active substitution windows).
	 */
	arrangeableTicketProjects(): "all" | readonly string[] {
		if (this.can("update", "Plan")) return "all";
		return this.editableTicketProjects();
	}

	/**
	 * Whether the caller may arrange one project's work on a board — `update Plan` or the
	 * project relationship.
	 *
	 * @param projectId - The project of the ticket being arranged.
	 * @param projectOrgId - The project's organisation, where the surface has it; a foreign one
	 *   refuses, as in `canEditTickets`.
	 * @returns True when either arm covers the project.
	 */
	canArrangeTickets(projectId: string, projectOrgId?: string | null): boolean {
		if (this.isForeignOrganization(projectOrgId)) return false;
		const reach = this.arrangeableTicketProjects();
		return reach === "all" || reach.includes(projectId);
	}

	/**
	 * The throwing form of `canArrangeTickets`, for the handlers that refuse with a 403.
	 *
	 * @param projectId - The project of the ticket being arranged.
	 * @throws ForbiddenError when neither arm covers the project.
	 */
	assertCanArrangeTickets(projectId: string): void {
		if (!this.canArrangeTickets(projectId)) {
			throw new ForbiddenError("arrange", "the project's work");
		}
	}

	// Expose raw ability for advanced checks if ever needed
	getAbility(): AppAbility {
		return this.ability;
	}

	get user(): PermissionUser {
		return {
			id: this.userId,
			orgId: this.orgId,
			isOwner: this.isOwner,
			projectIds: this.projectIds,
		};
	}

	async hasPermission(...checks: Array<() => boolean | Promise<boolean>>): Promise<boolean> {
		for (const check of checks) {
			const result = await check();
			if (result) return true;
		}
		return false;
	}
}
